import { SyntheticEvent, useContext, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { queryKeys } from "../../api/query";
import { useApproveInstitution } from "../../api/queries/institutions/useApproveInstitution";
import { Panel } from "primereact/panel";
import { userContext } from "../../context/user";
import { useMergeInstitutions } from "../../api/queries/institutions/useMergeInstitutions";
import {
  Institution,
  InstitutionMergePayload,
} from "../../api/types/institution";
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
} from "primereact/autocomplete";
import { useDebounce } from "../../common/useDebounce";
import { useInstitutionsSearch } from "../../api/queries/institutions/useInstitutionsSearch";
import { appToastContext } from "../../context/toast";

export default function InstitutionApproveForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useContext(userContext);
  const { ref: appToastRef } = useContext(appToastContext);
  const approveInstitution = useApproveInstitution();
  const mergeInstitution = useMergeInstitutions();

  // approve institution
  const [institutionToApproveOptions, setInstitutionToApproveOptions] =
    useState<Institution[]>([]);

  const [institutionToApprove, setInstitutionToApprove] = useState<string>("");
  const [institutionToApproveName, setInstitutionToApproveNameChange] =
    useState<string>("");
  const institutionToApproveNameDebounced = useDebounce(
    institutionToApproveName,
    500,
  );

  const onInstitutionToApproveNameChange = (
    event: AutoCompleteChangeEvent,
  ): void => {
    if (typeof event.value === "string") {
      setInstitutionToApproveNameChange(event.value);
    } else {
      const institution: Institution = event.value;
      setInstitutionToApproveNameChange(institution.name);
      setInstitutionToApprove(institution.id);
    }
  };

  const { data: institutionsToApproveResponse } = useInstitutionsSearch({
    offset: 0,
    limit: 100,
    search: institutionToApproveNameDebounced,
  });

  useEffect(() => {
    if (institutionsToApproveResponse) {
      setInstitutionToApproveOptions(
        institutionsToApproveResponse.data.results,
      );
    }
  }, [institutionsToApproveResponse]);

  // institutions to replace
  const [institutionToReplaceOptions, setInstitutionToReplaceOptions] =
    useState<Institution[]>([]);

  const [institutionsToReplace, setInstitutionsToReplace] = useState<
    Institution[]
  >([]);
  const [institutionToReplaceName, setInstitutionToReplaceName] =
    useState<string>("");
  const institutionToReplaceNameDebounced = useDebounce(
    institutionToReplaceName,
    500,
  );
  const onInstitutionToReplaceChange = (event: AutoCompleteChangeEvent) => {
    setInstitutionsToReplace(
      Array.isArray(event.value) ? (event.value as Institution[]) : [],
    );
    setInstitutionToReplaceName("");
    setInstitutionToReplaceOptions([]);
  };

  const { data: institutionsToReplaceResponse } = useInstitutionsSearch({
    offset: 0,
    limit: 100,
    search: institutionToReplaceNameDebounced,
  });

  useEffect(() => {
    if (institutionsToReplaceResponse) {
      setInstitutionToReplaceOptions(
        institutionsToReplaceResponse.data.results,
      );
    }
  }, [institutionsToReplaceResponse]);

  //
  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!institutionToApprove) {
      return;
    }

    await approveInstitution.mutateAsync(institutionToApprove);

    await approveInstitution.mutateAsync(institutionToApprove, {
      onSuccess: () => {
        if (appToastRef?.current) {
          appToastRef.current.show({
            severity: "success",
            summary: "Institution approved successfully.",
          });
        }
      },
    });

    if (institutionsToReplace && institutionsToReplace.length > 0) {
      const payload: InstitutionMergePayload = {
        keep: institutionToApprove,
        discard: institutionsToReplace.map((i) => i.id),
      };

      await mergeInstitution.mutateAsync(payload, {
        onSuccess: () => {
          if (appToastRef?.current) {
            appToastRef.current.show({
              severity: "success",
              summary: "Institutions merged successfully.",
            });
          }
        },
      });
    }

    queryClient.invalidateQueries(queryKeys.institutionList(), {
      refetchInactive: true,
    });

    navigate("/institutions");
  };

  const onCancel = () => navigate(-1);

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <Panel header="General" toggleable={true} className="mb-3">
        <div className="p-fluid">
          <div className="field pt-3">
            <div className="p-float-label">
              <AutoComplete
                value={institutionToApproveName}
                suggestions={institutionToApproveOptions}
                completeMethod={() => {}}
                onChange={onInstitutionToApproveNameChange}
                field="name"
              />
              <label>Institution to approve *</label>
            </div>
          </div>

          <div className="field pt-3">
            <div className="p-float-label">
              <AutoComplete
                multiple={true}
                value={institutionsToReplace}
                suggestions={institutionToReplaceOptions}
                completeMethod={(event: AutoCompleteCompleteEvent) => {
                  setInstitutionToReplaceName(event.query);
                }}
                onChange={onInstitutionToReplaceChange}
                field="name"
              />
              <label>Institutions to replace</label>
            </div>
          </div>
        </div>
      </Panel>

      <div className="flex gap-1 justify-content-end">
        <Button
          type="button"
          label="Cancel"
          className="p-button-sm p-button-secondary"
          text={true}
          onClick={onCancel}
        />

        {isAdmin() && (
          <Button
            type="submit"
            label="Save"
            className="p-button-main p-button-sm"
            disabled={!institutionToApprove}
          />
        )}
      </div>
    </form>
  );
}
